/** Global Imports */
import React, { useContext, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import DeleteIcon from '@material-ui/icons/Delete';
import GitHubIcon from '@material-ui/icons/GitHub';
import NotificationsOffIcon from '@material-ui/icons/NotificationsOff';

/** Component Imports */
import { styles } from './styles';
import {
  DeleteButton,
  DisabledButton,
  PrimaryButton,
} from 'components/common/buttons';

/** Context Imports */
import { userContext } from 'context/user';
import { useLoader } from 'context/loader';
import { useConfirmation } from 'context/confirmation';

/** Service Imports */
import {
  deleteProgram,
  deleteUserFromProgram,
  deleteProgramFromGit,
  migrateProgramToGit,
} from 'services/programs';
import EditProgram from '../edit-program';
import useApplicationPermission from 'components/common/hooks/useCollabPermission';

export default function ProgramSettings({
  programId,
  handlePostProgramDelete,
  updateProgramDetails,
}) {
  const classes = styles();
  const { dispatchLoader } = useLoader();
  const { dispatchConfirmation } = useConfirmation();
  const { currentUser } = useContext(userContext);
  const activeProgram = useSelector(
    (state) => state.programReducer.activeProgram,
  );
  const { collabSuperAdminRole } = useApplicationPermission();
  const isAdmin = useMemo(
    () =>
      collabSuperAdminRole !== undefined ||
      activeProgram.ownerId === currentUser.userId ||
      false,
    [activeProgram.ownerId, currentUser.userId, collabSuperAdminRole],
  );

  const { collabPlatformAdminRole } = useApplicationPermission();
  const isCollabPlatformAdmin = useMemo(
    () => collabPlatformAdminRole !== undefined,
  );

  const handleDeleteProgram = useCallback(() => {
    const accessRank = collabSuperAdminRole !== undefined ? 0 : undefined;
    dispatchConfirmation({
      show: true,
      title: 'Delete Program',
      message: `Do you really want to delete the program '${activeProgram.programName}'?`,
      onConfirmation: () => {
        dispatchLoader({ show: true, message: 'Deleting the program..' });
        deleteProgramFromGit(programId).then(() => {
          deleteProgram(programId, currentUser.userId, accessRank).then(
            (res) => {
              dispatchLoader({ show: false });
              handlePostProgramDelete();
            },
          );
        });
      },
    });
  }, [activeProgram.programName, programId, currentUser.userId]);

  const handleUnsubscribeProgram = useCallback(() => {
    dispatchConfirmation({
      show: true,
      title: 'Unsubscribe Program',
      message: `Do you really want to unsubscribe the program '${activeProgram.programName}'?`,
      onConfirmation: () => {
        dispatchLoader({ show: true, message: 'Unsubscribing the program..' });
        deleteUserFromProgram(programId, currentUser.userId).then((res) => {
          dispatchLoader({ show: false });
          handlePostProgramDelete();
        });
      },
    });
  }, []);

  const handleMigrateToGit = useCallback(() => {
    let navigationPath = `${
      process.env.REACT_APP_MFE_DESIGNER_ORIGIN || window.location.origin
    }/designer/#/program/${programId}`;
    dispatchConfirmation({
      show: true,
      title: 'Migrate to Git',
      message: `Do you really want to migrate this program '${activeProgram.programName}'? You’ll be redirected to the IPM Cloud Designer to track the progress. The migration process may take approximately ~5 minutes to complete.`,
      onConfirmation: () => {
        dispatchLoader({ show: true, message: 'Migrating the program..' });
        migrateProgramToGit(programId, 'programToGitMigration').then((res) => {
          dispatchLoader({ show: false });
          window.location.href = navigationPath;
        });
      },
    });
  }, []);

  return (
    <div className={classes.root}>
      <EditProgram updateProgramDetails={updateProgramDetails} />
      <div className={classes.bottomContainer}>
        {isAdmin ? (
          <>
            <DeleteButton
              onClick={handleDeleteProgram}
              startIcon={<DeleteIcon />}
            >
              Delete Program
            </DeleteButton>
            {isCollabPlatformAdmin && !activeProgram.gitProgram && (
              <PrimaryButton
                startIcon={<GitHubIcon />}
                onClick={handleMigrateToGit}
              >
                Migrate To GitHub
              </PrimaryButton>
            )}
          </>
        ) : (
          <div className={classes.userContainer}>
            <DisabledButton startIcon={<DeleteIcon />}>
              Delete Program
            </DisabledButton>
            <div className={classes.unsubscribe}>
              <PrimaryButton
                onClick={handleUnsubscribeProgram}
                startIcon={<NotificationsOffIcon />}
              >
                Unsubscribe
              </PrimaryButton>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
